import axios from 'axios';

import { WorkSessionExportFilters, WorkSessionsFilters, WorkSessionsResponse } from '../interfaces/WorkSession';
import { ServicoInacabado } from '../interfaces/AtividadeCarro';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const listWorkSessions = async (filters: WorkSessionsFilters): Promise<WorkSessionsResponse> => {
  const response = await axios.get(`${BACKEND_URL}/work-sessions`, {
    params: filters,
    headers: authHeader(),
    withCredentials: true,
  });

  return response.data;
};

export interface AtendimentosFilters {
  q?: string;
  esteiraId?: string;
  entryStartDate?: string;
  entryEndDate?: string;
  hasPending?: 'true';
  collaboratorId?: string;
  page?: number;
  limit?: number;
}

export interface AtendimentoResumo {
  carId: string;
  plate: string;
  model: string | null;
  entryDate: string | null;
  eventNumber: number | null;
  esteiraId: string | null;
  esteiraNome: string | null;
  esteiraChave: string | null;
  totalWorkedSeconds: number;
  activitiesCount: number;
  /** Horas finalizadas aguardando aprovação do Chefe. */
  pendingCount: number;
  /** at-46: serviços em aberto (inacabados ou em andamento) por esteira. */
  servicosInacabados?: ServicoInacabado[];
}

export interface AtendimentosResponse {
  total: number;
  page: number;
  limit: number;
  atendimentos: AtendimentoResumo[];
}

export const getAtendimentos = async (filters: AtendimentosFilters): Promise<AtendimentosResponse> => {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v != null),
  );

  const response = await axios.get(`${BACKEND_URL}/work-sessions/atendimentos`, {
    params: cleanFilters,
    headers: authHeader(),
    withCredentials: true,
  });

  return response.data;
};

/**
 * Inicia a atividade do colaborador no veículo.
 *
 * at-99 (ADR 0050): `esteiraId` é **opcional** e diz em qual esteira a hora será apontada.
 * Ausente = a esteira principal do veículo, que é o comportamento de sempre. Presente, precisa
 * ser uma esteira ATIVA do veículo (a principal ou uma paralela vigente) — é assim que o
 * mecânico aponta em Mecânica num veículo que está em Desmontagem.
 *
 * ⚠️ **Amarra de deploy: o backend sobe ANTES do frontend.** O `ValidationPipe` do backend tem
 * `forbidNonWhitelisted`, então backend velho recebendo `esteiraId` responde **400 na aba Meus
 * Veículos inteira** — não "campo ignorado". O campo só é enviado quando existe escolha, o que
 * reduz a janela, mas não a elimina.
 */
export const startWorkSession = async (
  carId: string,
  password: string,
  esteiraId?: string,
) => {
  const response = await axios.post(
    `${BACKEND_URL}/work-sessions/start`,
    { carId, password, ...(esteiraId ? { esteiraId } : {}) },
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

/**
 * Finaliza a atividade. `servicoConcluido` (at-46) declara o desfecho do serviço da esteira:
 * false = ficou inacabado (as horas vão para aprovação, mas o veículo não sai da etapa e
 * ninguém é desvinculado — o serviço fica como pendência retomável).
 */
export const stopWorkSession = async (
  id: string,
  password: string,
  servicoConcluido?: boolean,
  pausaObservacao?: string,
  /** at-134 (ADR 0065): o motivo da parada, quando o serviço fica inacabado. */
  motivoParadaId?: string,
) => {
  const response = await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/stop`,
    {
      password,
      ...(servicoConcluido !== undefined && { servicoConcluido }),
      ...(servicoConcluido === false && pausaObservacao ? { pausaObservacao } : {}),
      ...(servicoConcluido === false && motivoParadaId ? { motivoParadaId } : {}),
    },
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

export const pauseWorkSession = async (id: string, reason: string, password: string) => {
  const response = await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/pause`,
    { reason, password },
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

export const resumeWorkSession = async (id: string, password: string) => {
  const response = await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/resume`,
    { password },
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

export const approveWorkSession = async (id: string) => {
  const response = await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/approve`,
    {},
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

export const rejectWorkSession = async (id: string, rejectionReason?: string) => {
  const response = await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/reject`,
    { rejectionReason },
    { headers: authHeader(), withCredentials: true },
  );

  return response.data;
};

export const getPendingSLACount = async (): Promise<number> => {
  const response = await axios.get(`${BACKEND_URL}/work-sessions/pending-sla/count`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data?.count ?? 0;
};

export const getPendingSLACars = async (): Promise<any[]> => {
  const response = await axios.get(`${BACKEND_URL}/work-sessions/pending-sla/cars`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data ?? [];
};

export const getMyRejections = async (): Promise<any[]> => {
  const response = await axios.get(`${BACKEND_URL}/work-sessions/my-rejections`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data ?? [];
};

export const acknowledgeRejection = async (id: string): Promise<void> => {
  await axios.patch(
    `${BACKEND_URL}/work-sessions/${id}/acknowledge-rejection`,
    {},
    { headers: authHeader(), withCredentials: true },
  );
};

export const exportWorkSessionsCsv = async (filters: WorkSessionExportFilters): Promise<void> => {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v != null),
  );

  const response = await axios.get(`${BACKEND_URL}/work-sessions/export`, {
    params: cleanFilters,
    headers: authHeader(),
    withCredentials: true,
    responseType: 'blob',
  });

  const date = new Date().toISOString().split('T')[0];
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `relatorio-sla-${date}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
